import React, { useState, useRef, useEffect } from "react";
import { Box, TextField, IconButton, Paper, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import "./Chatbot.css";
// import Chatbot from 'https://cdn.jsdelivr.net/npm/@n8n/chat/dist/chat.bundle.es.js';



const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: "Hello! I'm here to help. How can I assist you today?",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Backend API configuration
  const CHATBOT_API_URL = process.env.REACT_APP_CHATBOT_API_URL || 
    "https://sarithm.app.n8n.cloud/webhook/6aa2cce9-0074-466f-9e88-b85b33576c56/chat";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      text: inputMessage,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const response = await fetch(CHATBOT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "sendMessage",
          chatInput: inputMessage,
          // conversation_history: messages,
          sessionId: "test-session-id"
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const botMessage = {
        text: data.output || "I'm sorry, I didn't understand that. Can you rephrase?",
        sender: "bot",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Chatbot error:", error);
      const errorMessage = {
        text: "Sorry, I'm having trouble connecting. Please try again later or contact us through the Contact page.",
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          className="chatbot-toggle-button"
          onClick={() => setIsOpen(true)}
          aria-label="Open chatbot"
        >
          <ChatIcon />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-container">
          <Paper className="chatbot-window" elevation={8}>
            {/* Header */}
            <Box className="chatbot-header">
              <Typography variant="h6" component="div">
                Chat with us
              </Typography>
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                aria-label="Close chatbot"
                sx={{ color: "white" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Messages */}
            <Box className="chatbot-messages">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`chatbot-message chatbot-message-${msg.sender}`}
                >
                  <div className="chatbot-message-content">
                    {msg.text}
                  </div>
                  <div className="chatbot-message-time">
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="chatbot-message chatbot-message-bot">
                  <div className="chatbot-message-content">
                    <span className="chatbot-typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Box className="chatbot-input-container">
              <TextField
                fullWidth
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                size="small"
                variant="outlined"
                className="chatbot-input"
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
                aria-label="Send message"
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Paper>
        </div>
      )}
    </>
  );
};

export default Chatbot;

